import { useVaultBalances } from '@app/hooks/plugins/useVaultBalances';
import { useMintState } from '@app/state/mintStore';
import { useMemo } from 'react';
import { Address } from '@thirdweb-dev/sdk';
import { useContract } from '@thirdweb-dev/react-core';
import { useAsync } from 'react-use';
import { getContract } from '@thirdweb-dev/react';
import { algebraPoolABI } from '../../../abis/v3';
import { Currency, Field } from '@cryptoalgebra/kim-sdk';
import { ethers } from 'ethers';
import { useSupportedChain } from '@app/hooks/other/useSupportedChain';

export function useTokensRatios(
  poolAddress: Address | undefined,
  currencyA: Currency | undefined,
  currencyB: Currency | undefined
) {
  const { strategy, independentField } = useMintState();

  const chainId = useSupportedChain()?.chainId;

  const { data } = useVaultBalances(strategy?.vaultAddress);

  const { contract: poolContract } = useContract(poolAddress, algebraPoolABI);

  const { value: sqrtPriceX96 } = useAsync(async () => {
    if (!poolContract) {
      return;
    }

    try {
      const res = await poolContract.call('globalState');

      return res[0];
    } catch (e) {
      console.error(e);
    }
  }, [poolContract]);

  const { value: poolTokens } = useAsync(async () => {
    if (!poolContract || !chainId) {
      return;
    }

    try {
      const token0 = await poolContract.call('token0');
      const token1 = await poolContract.call('token1');

      const token0Contract = await getContract({
        address: token0,
        contractTypeOrAbi: 'token',
        network: chainId
      });
      // @ts-ignore
      const token0Decimals = await token0Contract.call('decimals');

      const token1Contract = await getContract({
        address: token1,
        contractTypeOrAbi: 'token',
        network: chainId
      });
      // @ts-ignore
      const token1Decimals = await token1Contract.call('decimals');

      return {
        token0Address: token0,
        token1Address: token1,
        token0Decimals,
        token1Decimals
      };
    } catch (e) {
      console.error(e);
    }
  }, [poolContract]);

  const ratios = useMemo(() => {
    if (!data || !sqrtPriceX96 || !poolTokens || !currencyA || !currencyB) {
      return;
    }

    const { amountToken0, amountToken1 } = data;
    const _token0 = +ethers.utils.formatUnits(
      amountToken0,
      poolTokens.token0Decimals
    );
    const _token1 = +ethers.utils.formatUnits(
      amountToken1,
      poolTokens.token1Decimals
    );

    // swap tokens if needed
    const isSwappedPosition =
      poolTokens.token0Address.toLowerCase() ===
        currencyB.wrapped.address.toLowerCase() &&
      poolTokens.token1Address.toLowerCase() ===
        currencyA.wrapped.address.toLowerCase();
    const [token0, token1] = isSwappedPosition
      ? [_token1, _token0]
      : [_token0, _token1];

    let ratio: number;

    if (token0 === 0 || token1 === 0) {
      // Deposit Ratio for Vault when token balances are 0
      const price = sqrtPriceX96 ** 2 / 2 ** 192;

      const nativeTokenRatio =
        price / 10 ** (+poolTokens.token1Decimals - +poolTokens.token0Decimals);

      if (independentField === Field.CURRENCY_A && nativeTokenRatio !== 0) {
        ratio = 1 / nativeTokenRatio;
      } else {
        ratio = nativeTokenRatio;
      }

      return {
        ratio
      };
    }

    // Deposit Ratio for Vaults when token balances are greater than 0
    if (token0 !== 0 && token1 !== 0) {
      if (independentField === Field.CURRENCY_A) {
        if (token1 === 0) {
          ratio = token0;
        } else {
          ratio = token0 / token1;
        }
      } else {
        if (token0 === 0) {
          ratio = token1;
        } else {
          ratio = token1 / token0;
        }
      }

      return {
        ratio
      };
    }

    return {
      ratio: 0
    };
  }, [data, sqrtPriceX96, poolTokens, independentField, currencyA, currencyB]);

  return {
    data: ratios
  };
}
